import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Logo } from '@/components/ui/Logo';
import { OfflineBanner } from '@/components/system/SystemState';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3, Building2, CalendarDays, ChevronDown, ChevronsLeft, ChevronsRight, Compass, Home, Images, Inbox,
  Megaphone, Menu, Plus, SquarePen, Wand2, X,
} from 'lucide-react';
import { apiClient } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { useSubscription } from '@/hooks/useSubscription';
import { NotificationBell } from './NotificationBell';
import { cx, JobDock, JobPill, useDockCollapsed } from '@/components/growth-desk/ui';
import { DESK } from '@/components/growth-desk/links';
import { useDesk } from '@/components/growth-desk/data/DeskData';
import { useSetupDockShown } from '@/components/onboarding/setupDockShown';

/**
 * The app frame (app-structure board) for every signed-in page: one navy rail — the daily places on top,
 * Plan (set once) and Tools below, credits and the account menu at the bottom — a slim top
 * bar for page tools, background jobs and "needs you", and phone tabs under md.
 * Links to pages outside the desk go to today's routes.
 */
interface RailItem { label: string; to: string; icon: React.ElementType; match?: (path: string) => boolean; badge?: number }

const RAIL_KEY = 'gd.rail.collapsed';

const useRailCollapsed = () => {
  const [c, setC] = useState<boolean>(() => { try { return localStorage.getItem(RAIL_KEY) === '1'; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem(RAIL_KEY, c ? '1' : '0'); } catch { /* storage unavailable */ } }, [c]);
  return [c, setC] as const;
};

const initials = (name?: string | null) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join('');

/** A menu that floats over the page, anchored to its button: never clipped by the sidebar. */
const FloatingMenu: React.FC<{
  anchor: React.RefObject<HTMLElement>; place: 'below' | 'above'; onClose: () => void; children: React.ReactNode; className?: string;
}> = ({ anchor, place, onClose, children, className }) => {
  const menu = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<React.CSSProperties | null>(null);
  useLayoutEffect(() => {
    const at = () => {
      const r = anchor.current?.getBoundingClientRect();
      if (!r) return;
      const width = Math.max(r.width, 288);
      const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
      setPos(place === 'below'
        ? { left, top: r.bottom + 8, width, maxHeight: window.innerHeight - r.bottom - 16 }
        : { left, bottom: window.innerHeight - r.top + 8, width, maxHeight: r.top - 16 });
    };
    at();
    window.addEventListener('resize', at);
    window.addEventListener('scroll', at, true);
    return () => { window.removeEventListener('resize', at); window.removeEventListener('scroll', at, true); };
  }, [anchor, place]);
  useEffect(() => {
    const down = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!menu.current?.contains(t) && !anchor.current?.contains(t)) onClose();
    };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', down);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key); };
  }, [anchor, onClose]);
  if (!pos) return null;
  return createPortal(
    <div ref={menu} role="menu" style={pos} className={cx('fixed z-[1100] overflow-y-auto rounded-card bg-card p-2 text-ink shadow-decide', className)}>{children}</div>,
    document.body,
  );
};

const BusinessSwitcher: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
  const { currentOrganization, switchOrganization } = useAuth();
  const [open, setOpen] = useState(false);
  const [orgs, setOrgs] = useState<{ id: string; name: string }[] | null>(null);
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open || orgs) return;
    apiClient.get('/api/v1/organizations').then(r => setOrgs(r.data || [])).catch(() => setOrgs([]));
  }, [open, orgs]);
  const name = currentOrganization?.name || 'Your business';
  return (
    <div className="relative">
      <button ref={ref} type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-haspopup="menu"
        title={collapsed ? `${name} · switch business` : undefined}
        className={cx('flex w-full items-center gap-2.5 rounded-btn bg-navy-2 text-left text-on-navy', collapsed ? 'justify-center p-2' : 'min-h-[52px] px-3 py-2')}>
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-work-dot font-display text-sm font-extrabold text-on-navy">{initials(name).slice(0, 1)}</span>
        {!collapsed && <>
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-sm font-semibold" title={name}>{name}</span>
            <span className="text-xs text-on-navy-3">Switch business</span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-on-navy-3" aria-hidden="true" />
        </>}
      </button>
      {open && (
        <FloatingMenu anchor={ref} place="below" onClose={() => setOpen(false)}>
          {orgs === null ? <div className="p-3 text-small text-ink-3">Loading…</div> : orgs.map(o => (
            <button key={o.id} role="menuitem" type="button"
              onClick={async () => { setOpen(false); if (o.id !== currentOrganization?.id) await switchOrganization(o.id); }}
              className={cx('flex w-full items-center gap-2.5 rounded-btn px-3 py-2.5 text-left text-sm hover:bg-ground', o.id === currentOrganization?.id && 'font-bold')}>
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-inset font-display text-xs font-extrabold">{initials(o.name).slice(0, 1)}</span>
              <span className="min-w-0 flex-1 break-words">{o.name}</span>
              {o.id === currentOrganization?.id && <span className="shrink-0 text-xs font-semibold text-ok">Current</span>}
            </button>
          ))}
          <Link to={DESK.businesses} role="menuitem" onClick={() => setOpen(false)} className="mt-1 block rounded-btn border-t border-line px-3 py-2.5 text-sm font-semibold text-brand-ink hover:bg-ground">Businesses & connections</Link>
        </FloatingMenu>
      )}
    </div>
  );
};

const AccountMenu: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  if (!user) return null;
  const item = 'block w-full rounded-btn px-3 py-2.5 text-left text-sm text-ink hover:bg-ground';
  return (
    <div className="relative">
      <button ref={ref} type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-haspopup="menu"
        className={cx('flex w-full items-center gap-2.5 rounded-btn text-left text-on-navy hover:bg-navy-2', collapsed ? 'justify-center p-1.5' : 'px-2 py-1.5')}>
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-navy-ink font-display text-sm font-bold text-card">{initials(user.name)}</span>
        {!collapsed && <span className="flex min-w-0 flex-col"><span className="truncate text-sm font-semibold">{user.name}</span><span className="truncate text-xs text-on-navy-3">{user.email}</span></span>}
      </button>
      {open && (
        <FloatingMenu anchor={ref} place="above" onClose={() => setOpen(false)}>
          <div className="px-3 py-2"><div className="break-words text-sm font-semibold text-ink">{user.name}</div><div className="break-all text-xs text-ink-3">{user.email}</div></div>
          <div className="my-1 border-t border-line" />
          <Link role="menuitem" to={DESK.settings} onClick={() => setOpen(false)} className={item}>Settings</Link>
          <Link role="menuitem" to={DESK.businesses} onClick={() => setOpen(false)} className={item}>Businesses & connections</Link>
          <Link role="menuitem" to={DESK.team} onClick={() => setOpen(false)} className={item}>Team</Link>
          <Link role="menuitem" to={DESK.billing} onClick={() => setOpen(false)} className={item}>Plan, billing & credits</Link>
          <div className="flex items-center justify-between gap-2 px-3 py-2 text-sm text-ink">
            Theme
            <span role="group" aria-label="Theme" className="inline-flex gap-1 rounded-btn bg-inset p-1">
              {(['light', 'dark'] as const).map(t => (
                <button key={t} type="button" aria-pressed={theme === t} onClick={() => setTheme(t)}
                  className={cx('min-h-[30px] rounded-lg px-2.5 text-xs font-semibold capitalize', theme === t ? 'bg-navy text-on-navy' : 'text-ink')}>{t}</button>
              ))}
            </span>
          </div>
          <div className="my-1 border-t border-line" />
          <button role="menuitem" type="button" className={cx(item, 'text-bad')} onClick={async () => { await logout(); navigate('/login'); }}>Sign out</button>
        </FloatingMenu>
      )}
    </div>
  );
};

const Credits: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
  const { subscription } = useSubscription();
  const c = subscription?.credits;
  if (!c) return null;
  const unlimited = c.available_credits === -1;
  const total = (c.total_credits > 0 ? c.total_credits : 0) + (c.bonus_credits || 0);
  const left = Math.max(0, c.available_credits);
  if (collapsed) return (
    <Link to={DESK.billing} title={`${unlimited ? 'Unlimited' : left.toLocaleString('en-IN')} credits left`} className="mx-auto grid h-11 w-11 place-items-center rounded-btn bg-navy-2 font-money text-[11px] font-semibold text-on-navy no-underline">
      {unlimited ? '∞' : left >= 1000 ? `${Math.floor(left / 100) / 10}k` : left}
    </Link>
  );
  return (
    <Link to={DESK.billing} className="flex flex-col gap-2 rounded-[14px] bg-navy-2 p-3 text-on-navy no-underline hover:bg-navy-3">
      <span className="flex items-baseline justify-between"><span className="text-xs text-on-navy-3">Credits left</span>
        <span className="font-money text-base font-semibold">{unlimited ? 'Unlimited' : left.toLocaleString('en-IN')}</span></span>
      {!unlimited && total > 0 && (
        <span className="h-1.5 overflow-hidden rounded-full bg-navy-3"><span className="block h-full rounded-full bg-brand" style={{ width: `${Math.min(100, (left / total) * 100)}%` }} /></span>
      )}
      <span className="text-xs leading-snug text-on-navy-2">Nothing is spent or published until you click approve.</span>
    </Link>
  );
};

/** A month's posts, a new post, or a post's own page. */
const isPostsPath = (p: string) => /^\/month\/[^/]+\/posts(\/|$)/.test(p) || p.startsWith('/posts');

const Rail: React.FC<{ collapsed: boolean; onToggle?: () => void; onNavigate?: () => void }> = ({ collapsed, onToggle, onNavigate }) => {
  const { pathname } = useLocation();
  const daily: RailItem[] = [
    { label: 'This month', to: DESK.home(), icon: Home, match: p => p === '/' || (p.startsWith('/month/') && !isPostsPath(p)) || p === '/needs' },
    { label: 'Campaigns', to: DESK.campaigns, icon: Megaphone, match: p => p.startsWith('/campaigns') },
    { label: 'Posts', to: DESK.posts(), icon: SquarePen, match: isPostsPath },
    { label: 'Calendar', to: DESK.calendar, icon: CalendarDays },
    { label: 'Results', to: DESK.results, icon: BarChart3 },
  ];
  const plan: RailItem[] = [
    { label: 'Strategy', to: DESK.strategy, icon: Compass, match: p => p.startsWith('/strategy') },
    { label: 'Your business', to: DESK.business, icon: Building2, match: p => p.startsWith('/business') },
  ];
  const tools: RailItem[] = [
    { label: 'Ad Studio', to: DESK.studio, icon: Wand2 },
    { label: 'Media Library', to: DESK.media, icon: Images },
    { label: 'Leads inbox', to: DESK.leads, icon: Inbox },
  ];
  const isOn = (it: RailItem) => (it.match ? it.match(pathname) : pathname === it.to.split('?')[0]);
  const link = (it: RailItem) => {
    const on = isOn(it);
    const Icon = it.icon;
    return (
      <Link key={it.label} to={it.to} onClick={onNavigate} aria-current={on ? 'page' : undefined} title={collapsed ? it.label : undefined}
        className={cx('flex min-h-[44px] items-center gap-3 rounded-[10px] text-sm font-semibold md:min-h-[38px] no-underline transition-colors',
          collapsed ? 'justify-center px-0' : 'px-3.5', on ? 'bg-brand text-brand-on' : 'text-on-navy-2 hover:bg-navy-2 hover:text-on-navy')}>
        <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
        {!collapsed && <span className="truncate">{it.label}</span>}
        {!collapsed && !!it.badge && <span className="ml-auto font-money text-xs">{it.badge}</span>}
      </Link>
    );
  };
  const group = (title: string, items: RailItem[]) => (
    <nav aria-label={title} className="flex flex-col gap-0.5">
      {!collapsed && <div className="px-3.5 pb-1 font-display text-label uppercase tracking-[0.12em] text-on-navy-3">{title}</div>}
      {items.map(link)}
    </nav>
  );
  return (
    <div className={cx('flex h-full flex-col gap-4 overflow-y-auto bg-navy py-4 text-on-navy', collapsed ? 'w-[72px] px-3' : 'w-[248px] px-4')}>
      <div className={cx('flex items-center', collapsed ? 'flex-col gap-3' : 'justify-between px-1.5')}>
        <Logo to={DESK.home()} onClick={onNavigate} onDark mark={collapsed} height={collapsed ? 30 : 26} />
        {onToggle && (
          <button type="button" onClick={onToggle} aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            className="grid h-9 w-9 place-items-center rounded-lg text-on-navy-3 hover:bg-navy-2 hover:text-on-navy">
            {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
          </button>
        )}
      </div>
      <BusinessSwitcher collapsed={collapsed} />
      <Link to={DESK.newCampaign} onClick={onNavigate} title={collapsed ? 'New campaign' : undefined}
        className={cx('flex min-h-[44px] items-center justify-center gap-2 rounded-btn bg-navy-3 md:min-h-[40px] font-display text-sm font-bold text-on-navy no-underline hover:bg-navy-2')}>
        <Plus className="h-4 w-4" aria-hidden="true" />{!collapsed && 'New campaign'}
      </Link>
      {group('Growth Desk', daily)}
      {group('Plan', plan)}
      {group('Tools', tools)}
      <div className="mt-auto flex flex-col gap-2">
        <Credits collapsed={collapsed} />
        <AccountMenu collapsed={collapsed} />
      </div>
    </div>
  );
};

/** Top bar: the page's own tools on the left; background jobs and what needs you on the right. */
const TopBar: React.FC<{ title?: string; tools?: React.ReactNode; onOpenJobs: () => void; onMenu: () => void }> = ({ title, tools, onOpenJobs, onMenu }) => {
  const { jobs, needs } = useDesk();
  const urgent = needs.length;
  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 px-5 pb-2 pt-5 md:px-12 md:pt-7">
      <div className="flex min-w-0 items-center gap-2.5">
        <button type="button" onClick={onMenu} aria-label="Open menu" className="grid h-10 w-10 place-items-center rounded-btn text-ink shadow-ring-line md:hidden">
          <Menu className="h-5 w-5" />
        </button>
        {title && <h1 className="m-0 truncate font-display text-xl font-extrabold tracking-[-0.02em] text-ink">{title}</h1>}
        {tools}
      </div>
      <div className="flex items-center gap-2.5">
        <JobPill count={jobs.length} onClick={onOpenJobs} />
        {urgent > 0 && (
          <Link to={DESK.needs} className="hidden min-h-[36px] items-center gap-2 rounded-full bg-bad-soft px-3 text-small font-semibold text-bad no-underline sm:inline-flex">
            Needs you · <span className="font-money">{urgent}</span>
          </Link>
        )}
        <NotificationBell />
      </div>
    </div>
  );
};

/** Needs-you count and the notification bell, for pages that carry them in their own header. */
export const HeaderStatus: React.FC = () => {
  const { needs } = useDesk();
  return (
    <div className="flex items-center gap-2">
      {needs.length > 0 && (
        <Link to={DESK.needs} className="inline-flex min-h-[36px] items-center gap-2 rounded-full bg-bad-soft px-3 text-small font-semibold text-bad no-underline">
          Needs you · <span className="font-money">{needs.length}</span>
        </Link>
      )}
      <NotificationBell />
    </div>
  );
};

/** Phone tabs for the monthly loop; More opens the full rail as a sheet. */
const PhoneTabs: React.FC<{ onMore: () => void }> = ({ onMore }) => {
  const { pathname } = useLocation();
  const { needs } = useDesk();
  const tab = (label: string, to: string, Icon: React.ElementType, on: boolean, badge?: number) => (
    <Link to={to} aria-current={on ? 'page' : undefined}
      className={cx('relative flex flex-1 flex-col items-center gap-1 py-2 text-[11px] font-semibold no-underline', on ? 'text-brand' : 'text-on-navy-3')}>
      <Icon className="h-5 w-5" aria-hidden="true" />{label}
      {!!badge && <span className="absolute right-[calc(50%-20px)] top-1 grid h-4 min-w-4 place-items-center rounded-full bg-bad px-1 font-money text-[10px] text-card">{badge}</span>}
    </Link>
  );
  return (
    <nav aria-label="Main" className="fixed inset-x-0 bottom-0 z-[1020] flex bg-navy pb-[env(safe-area-inset-bottom)] md:hidden">
      {tab('Month', DESK.home(), Home, pathname === '/' || (pathname.startsWith('/month/') && !isPostsPath(pathname)))}
      {tab('Posts', DESK.posts(), SquarePen, isPostsPath(pathname))}
      {tab('Calendar', DESK.calendar, CalendarDays, pathname.startsWith('/calendar'))}
      {tab('Needs you', DESK.needs, Inbox, pathname.startsWith('/needs'), needs.length)}
      <button type="button" onClick={onMore} className="flex flex-1 flex-col items-center gap-1 py-2 text-[11px] font-semibold text-on-navy-3">
        <Menu className="h-5 w-5" aria-hidden="true" />More
      </button>
    </nav>
  );
};

// Full-height work surfaces keep the screen to themselves on phones (no bottom tabs over their input).
const FULL_BLEED = [/^\/campaigns\/[^/]+/];
// Pages whose own header carries needs + notifications on desktop (campaign page board).
const OWN_HEADER = [/^\/campaigns\/(?!new$)[^/]+$/];

export interface AppLayoutProps {
  children: React.ReactNode;
  /** Shown in the top bar by pages that have no page header of their own yet. */
  pageTitle?: string;
  /** The page's own tools, left of the top bar (month switcher, list toggle…). */
  tools?: React.ReactNode;
  topBarAction?: React.ReactNode;
  /** Kept for existing callers; the business switcher lives in the menu now. */
  showBusinessSelector?: boolean;
  /** Focused flows (connect a platform): no menu, only "Back to Admeru". */
  focused?: boolean;
}

/**
 * The frame (menu, top bar, job dock, phone tabs) is mounted once by AppFrameHost, above the
 * routes, so it survives navigation: pages only swap what's inside it. Each page's AppLayout
 * registers with the host and portals its content into the frame's main area and its tools into
 * the top bar. Without a host (tests, odd callers) AppLayout renders a frame of its own.
 */
interface FrameSlot { id: number; title?: string }
const FrameApi = React.createContext<{ add: (s: FrameSlot) => void; remove: (id: number) => void } | null>(null);
const FrameTargets = React.createContext<{ main: HTMLElement | null; tools: HTMLElement | null }>({ main: null, tools: null });
let nextSlot = 1;

const Frame: React.FC<{ pageTitle?: string; tools?: React.ReactNode; toolsRef?: (el: HTMLElement | null) => void; mainRef?: (el: HTMLElement | null) => void; children?: React.ReactNode }> = ({
  pageTitle, tools, toolsRef, mainRef, children,
}) => {
  const [collapsed, setCollapsed] = useRailCollapsed();
  const [sheet, setSheet] = useState(false);
  const [dockCollapsed, setDockCollapsed] = useDockCollapsed();
  const setupShown = useSetupDockShown();
  const { jobs } = useDesk();
  const { pathname } = useLocation();
  const main = useRef<HTMLElement | null>(null);
  useEffect(() => { setSheet(false); }, [pathname]);
  // The main area now outlives pages: start each page at the top, as a fresh page would.
  useLayoutEffect(() => { main.current?.scrollTo(0, 0); }, [pathname]);
  const openJobs = useCallback(() => setDockCollapsed(false), [setDockCollapsed]);
  const bleed = FULL_BLEED.some(r => r.test(pathname));
  const ownHeader = OWN_HEADER.some(r => r.test(pathname));
  const setMain = useCallback((el: HTMLElement | null) => { main.current = el; mainRef?.(el); }, [mainRef]);

  return (
    <div className="flex h-[100dvh] w-full overflow-hidden bg-ground">
      <aside aria-label="Main" className="hidden shrink-0 md:block">
        {/* Work surfaces (campaign page, Ad Studio) keep the menu as icons, as on the boards. */}
        <Rail collapsed={collapsed} onToggle={() => setCollapsed(c => !c)} />
      </aside>
      {sheet && (
        <div className="fixed inset-0 z-[1040] flex md:hidden" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="h-full shadow-decide"><Rail collapsed={false} onNavigate={() => setSheet(false)} /></div>
          <button type="button" aria-label="Close menu" onClick={() => setSheet(false)} className="flex-1 bg-navy/50">
            <X className="ml-auto mr-4 mt-4 h-6 w-6 text-on-navy" />
          </button>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <div className={ownHeader ? 'hidden' : undefined}>
          <TopBar title={pageTitle} tools={toolsRef ? <span ref={toolsRef} className="contents" /> : tools} onOpenJobs={openJobs} onMenu={() => setSheet(true)} />
        </div>
        <OfflineBanner />
        <main ref={setMain} className={cx('min-w-0 flex-1 overflow-y-auto', !bleed && 'pb-20 md:pb-0')}>{children}</main>
      </div>
      {/* Above the Get set up dock when it is on screen (it shrinks to its header while jobs run). */}
      <JobDock jobs={jobs} collapsed={dockCollapsed} onCollapsedChange={setDockCollapsed}
        className={setupShown ? 'bottom-[76px] sm:bottom-[76px]' : bleed ? 'bottom-6' : 'bottom-20 md:bottom-6'} />
      {!bleed && <PhoneTabs onMore={() => setSheet(true)} />}
    </div>
  );
};

/** Mount once, around the routes (inside the router and providers). */
export const AppFrameHost: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [slots, setSlots] = useState<FrameSlot[]>([]);
  const [main, setMain] = useState<HTMLElement | null>(null);
  const [tools, setTools] = useState<HTMLElement | null>(null);
  const api = React.useMemo(() => ({
    add: (s: FrameSlot) => setSlots(prev => [...prev.filter(x => x.id !== s.id), s]),
    remove: (id: number) => setSlots(prev => prev.filter(x => x.id !== id)),
  }), []);
  const targets = React.useMemo(() => ({ main, tools }), [main, tools]);
  const top = slots[slots.length - 1];
  return (
    <FrameApi.Provider value={api}>
      <FrameTargets.Provider value={targets}>
        {children}
        {top && <Frame pageTitle={top.title} mainRef={setMain} toolsRef={setTools} />}
      </FrameTargets.Provider>
    </FrameApi.Provider>
  );
};

/** A page inside the shared frame: registers, then portals content and tools into it. */
const FramedPage: React.FC<Omit<AppLayoutProps, 'focused' | 'showBusinessSelector'>> = ({ children, pageTitle, tools, topBarAction }) => {
  const api = React.useContext(FrameApi)!;
  const { main, tools: toolsEl } = React.useContext(FrameTargets);
  const id = useRef(0);
  if (!id.current) id.current = nextSlot++;
  // Layout effects: a page leaving and the next arriving land in the same commit, so the frame never blinks.
  useLayoutEffect(() => {
    api.add({ id: id.current, title: pageTitle });
    return () => api.remove(id.current);
  }, [api, pageTitle]);
  const bar = (tools || topBarAction) ? <>{topBarAction && <span className="md:hidden">{topBarAction}</span>}{tools}</> : null;
  return <>{main && createPortal(children, main)}{toolsEl && bar && createPortal(bar, toolsEl)}</>;
};

export const AppLayout: React.FC<AppLayoutProps> = ({ children, pageTitle, tools, topBarAction, focused }) => {
  const hosted = React.useContext(FrameApi) !== null;
  if (focused) return (
    <div className="flex min-h-[100dvh] w-full flex-col bg-ground">
      <div className="flex items-center justify-between gap-4 px-5 py-5 md:px-12"><Logo height={28} /><Link to="/" className="text-sm font-semibold text-brand-ink no-underline hover:underline">← Back to Admeru</Link></div>
      <main className="min-w-0 flex-1">{children}</main>
    </div>
  );
  if (hosted) return <FramedPage pageTitle={pageTitle} tools={tools} topBarAction={topBarAction}>{children}</FramedPage>;
  return <Frame pageTitle={pageTitle} tools={<>{topBarAction && <span className="md:hidden">{topBarAction}</span>}{tools}</>}>{children}</Frame>;
};

/** Suspense fallback for pages in the frame: the frame stays, the spinner sits in the content area. */
export const FramePageLoading: React.FC = () => (
  <AppLayout>
    <div className="flex h-full min-h-[40vh] items-center justify-center" aria-busy="true" aria-label="Loading">
      <span className="h-6 w-6 animate-spin rounded-full border-2 border-line border-t-brand" />
    </div>
  </AppLayout>
);

export default AppLayout;
